import { CreateCustomerGroup } from '../../../admin/customergroup/components/CreateCustomerGroup';
import { CreatePortForm } from '../../../admin/port/list/components/CreatePortForm';
import { CreateSupplierGroup } from '../../../admin/suppliergroup/components/CreateSupplierGroup';
import { CreateAgentForm } from '../../../agent/CreateAgentForm/CreateAgentForm';
import { CreateAssignmentDrawer } from '../../../assignment/common/components/CreateAssignmentDrawer';
import { useDrawer } from '../../../cdl/Drawer/useDrawer.hook';
import { useToasts } from '../../../cdl/Toast/useToasts';
import { CreateKeyPortForm } from '../../../keyport/CreateKeyPortForm';
import { useCreateProductDrawer } from '../../../product/overview/hooks/useCreateProductDrawer';
import { translate } from '../../helpers/translate.helper';
import { useRole } from '../../hooks/useRole';
import { useVesselCreateModal } from '../../hooks/useVesselCreateModal';

interface GlobalCreateAction {
    id: string;
    label: string;
    onSelect: () => void;
}

export const useGlobalCreateActions = (): GlobalCreateAction[] => {
    const { push, pop } = useDrawer();
    const role = useRole();
    const { addToast } = useToasts();
    const { open: openCreateVesselDrawer } = useVesselCreateModal();
    const openCreateProductDrawer = useCreateProductDrawer();

    const companyId = role.isOneCompanyUser() ? role.getCompanies().map((company) => company.id)[0] : undefined;

    const openCreateAgentDrawer = () => {
        const drawerId = push({
            header: { title: translate('agents.createAgent') },
            content: (
                <CreateAgentForm
                    data={{
                        name: '',
                        portIds: [],
                        customerId: companyId,
                    }}
                    onSuccess={() => pop(drawerId)}
                    showCompanySelect={role.isSuperAdmin() || !role.isOneCompanyUser()}
                />
            ),
        });
    };

    const openCreatePortDrawer = () => {
        const drawerId = push({
            header: { title: translate('port.createTitle') },
            content: (
                <CreatePortForm
                    onSuccess={() => {
                        pop(drawerId);
                        addToast(translate('port.createPortSuccessToast'));
                    }}
                />
            ),
        });
    };

    const openCreateCustomerGroupDrawer = () => {
        const drawerId = push({
            header: { title: translate('customergroup.createtitle') },
            content: (
                <CreateCustomerGroup
                    onSuccess={() => {
                        pop(drawerId);
                        addToast(translate('customergroup.createCustomerGroupSuccessToast'));
                    }}
                />
            ),
        });
    };

    const openCreateSupplierGroupDrawer = () => {
        const drawerId = push({
            header: { title: translate('suppliergroups.create') },
            content: (
                <CreateSupplierGroup
                    onSuccess={() => {
                        pop(drawerId);
                        addToast(translate('suppliergroup.createSupplierGroupSuccessToast'));
                    }}
                />
            ),
        });
    };

    const openCreateAssignmentDrawer = () => {
        const drawerId = push({
            header: { title: translate('assignments.admin.invite') },
            content: (
                <CreateAssignmentDrawer
                    onSuccess={() => {
                        pop(drawerId);
                        addToast(translate('assignments.toasts.createSuccess'));
                    }}
                />
            ),
        });
    };

    const openCreateKeyPortDrawer = () => {
        const drawerId = push({
            header: { title: translate('keyPorts.addKeyPort') },
            content: <CreateKeyPortForm onSuccess={() => pop(drawerId)} />,
        });
    };

    if (!role.isSuperAdmin()) {
        return [];
    }

    return [
        {
            id: 'create-vessel',
            label: translate('shortcutMenu.vessel'),
            onSelect: openCreateVesselDrawer,
        },
        {
            id: 'create-agent',
            label: translate('shortcutMenu.agent'),
            onSelect: openCreateAgentDrawer,
        },
        {
            id: 'create-product',
            label: translate('shortcutMenu.product'),
            onSelect: openCreateProductDrawer,
        },
        {
            id: 'create-port',
            label: translate('shortcutMenu.port'),
            onSelect: openCreatePortDrawer,
        },
        {
            id: 'create-customer-group',
            label: translate('shortcutMenu.customerGroup'),
            onSelect: openCreateCustomerGroupDrawer,
        },
        {
            id: 'create-supplier-group',
            label: translate('shortcutMenu.supplierGroup'),
            onSelect: openCreateSupplierGroupDrawer,
        },
        {
            id: 'create-assignment',
            label: translate('shortcutMenu.assignment'),
            onSelect: openCreateAssignmentDrawer,
        },
        {
            id: 'create-key-port',
            label: translate('shortcutMenu.keyPort'),
            onSelect: openCreateKeyPortDrawer,
        },
    ];
};
